import { FormikErrors, FormikTouched, useFormik } from 'formik'
import React from 'react'
import * as Yup from 'yup'
import { JumboSelect } from '../../../../../components/JumboSelect/JumboSelect'
import { LoadingButton } from '../../../../../components/LoadingButton/LoadingButton'
import { ROUTES } from '../../../../../constants/routes'
import { useRouter } from '../../../../../hooks/useRouter'
import translations from '../../../../../translations/en.json'
import { AmountOfObjects } from '../hooks/useHandleImport'

const invalidFeedback = (
  key: keyof IValue,
  touched: FormikTouched<IValue>,
  errors: FormikErrors<IValue>,
) => (touched[key] && !!errors[key] ? errors[key] : null)

interface IValue {
  amount: AmountOfObjects
}

interface IProps
  extends Omit<
    React.DetailedHTMLProps<React.FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>,
    'onSubmit'
  > {
  initialValues?: IValue
  onSubmit: (values: IValue) => Promise<void> | void
}

export const ChooseAmountofObjectsStep = ({ initialValues, onSubmit, ...props }: IProps) => {
  const router = useRouter()
  const { errors, handleSubmit, isSubmitting, setFieldValue, setFieldError, touched, values } =
    useFormik<IValue>({
      initialValues: {
        amount: initialValues?.amount as unknown as IValue['amount'],
      },
      validationSchema: Yup.object().shape({
        amount: Yup.mixed<AmountOfObjects>()
          .oneOf(['one', 'many'], (translations as any)['error.default_inputs_message'])
          .required((translations as any)['error.import_amount_required']),
      }),
      onSubmit: async (value, { setSubmitting }) => {
        try {
          await onSubmit(value)
        } catch (e: any) {
          if (e.param) setFieldError(e.param, e.message)
        }
        setSubmitting(false)
      },
    })
  return (
    <form className="py-3" onSubmit={handleSubmit} {...props}>
      <h5 className="mb-4">How many item types are you importing?</h5>
      <p>
        These are the data types used to organize your info. Common items are contacts, attendees,
        registrations, and more.
      </p>
      <div className="mb-3">
        <JumboSelect
          value={values.amount}
          types={[
            {
              name: 'amount',
              title: 'One item type',
              description: 'For example, your file only has data on contacts.',
              value: 'one',
            },
            {
              name: 'amount',
              title: 'Multiple item types',
              description: (
                <>
                  For example, your file has data on contacts and registrations.
                  <span className="badge rounded-pill text-bg-light border ms-2">Coming soon</span>
                </>
              ),
              value: 'many',
              disabled: true,
            },
          ]}
          onChange={(value: AmountOfObjects) => {
            setFieldValue('amount', value, true)
          }}
        />
        <div className="invalid-feedback d-block">
          {invalidFeedback('amount', touched, errors) || '\u00A0'}
        </div>
      </div>
      <div className="mb-3 d-flex justify-content-between mt-4">
        <button
          type="button"
          className="btn btn-link btn-block d-md-inline-block w-md-auto mt-2 px-0"
          onClick={() => {
            if (!router.history.isEmpty) {
              router.history.goBack()
            } else {
              router.push(ROUTES.DASHBOARD)
            }
          }}
        >
          Cancel
        </button>
        <LoadingButton
          type="submit"
          className="btn btn-primary btn-block d-md-inline-block w-md-auto"
          loading={isSubmitting}
        >
          Continue
        </LoadingButton>
      </div>
    </form>
  )
}
